import axios from "axios";

// Base API host comes from the environment (see frontend/.env -> VITE_API_URL).
// Falls back to the local backend dev server if it's not set. Exported so
// any other module that needs to build an absolute URL to this backend
// (e.g. resolving an uploaded file's relative path into an <img src>) uses
// the exact same host instead of a separately hardcoded one.
export const API_HOST = (import.meta.env.VITE_API_URL || "http://localhost:5009").replace(/\/$/, "");
const API_BASE_URL = `${API_HOST}/api`;

// Base configuration for all API requests
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add an interceptor for adding the token if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request OTP endpoint
export const createAccount = async (email, password, role, firstName, lastName) => {
  try {
    const payload = { email, password, role, firstName, lastName };
    const response = await apiClient.post("/elearn/auth/create-account", payload);
    return response.data;
  } catch (error) {
    console.error("Signup API error:", error.response?.data || error.message);
    throw error;
  }
};

// Login endpoint
export const loginUser = async (email, password) => {
  try {
    const payload = { email, password };
    const response = await apiClient.post("/elearn/auth/login", payload);
    return response.data;
  } catch (error) {
    console.log("Response data:", error);
    throw error;
  }
};

// Sign in / sign up with a Google ID token (from Google Identity Services)
export const googleAuthApi = async (googleIdToken) => {
  try {
    const response = await apiClient.post("/elearn/auth/google", { token: googleIdToken });
    return response.data;
  } catch (error) {
    console.log("Google auth error:", error.response?.data || error.message);
    throw error;
  }
};

// Fetch the current authenticated user's full profile (used to rehydrate
// the session on page load, since a decoded JWT alone lacks name/avatar/etc.)
export const getUserDetails = async () => {
  try {
    const response = await apiClient.get("/elearn/auth/me");
    return response.data;
  } catch (error) {
    console.log("Get user details error:", error.response?.data || error.message);
    throw error;
  }
};

// Password reset is a 3-step OTP flow on the backend:
// 1) request an OTP be emailed  2) verify the OTP  3) set the new password

// Step 1: Send a password reset OTP to the user's email
export const sendResetOtp = async (email) => {
  try {
    const response = await apiClient.post("/elearn/auth/reset/send-otp", { email });
    return response.data;
  } catch (error) {
    console.log("Send reset OTP error:", error.response?.data || error.message);
    throw error;
  }
};

// Step 2: Verify the OTP the user received by email
export const verifyResetOtp = async (email, otp) => {
  try {
    const response = await apiClient.post("/elearn/auth/reset/verify-otp", { email, otp });
    return response.data;
  } catch (error) {
    console.log("Verify reset OTP error:", error.response?.data || error.message);
    throw error;
  }
};

// Step 3: Set the new password (only succeeds after OTP verification)
export const setNewPassword = async (email, password) => {
  try {
    const response = await apiClient.post("/elearn/auth/reset/set-password", { email, password });
    return response.data;
  } catch (error) {
    console.log("Set new password error:", error.response?.data || error.message);
    throw error;
  }
};

export const createCourse = async (formData) => {
  try {
    const response = await apiClient.post("/elearn/courses", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
    return response.data;
  } catch (error) {
    console.log("Course not created", error.response?.data || error.message);
    throw error;
  }
};

export const updateCourseApi = async (id, formData) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
    return response.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};

// `params` is optional (e.g. { organizationId } to scope the public catalog
// to one organization -- see the org-branded catalog page) and passed
// straight through as query params; every existing caller that omits it
// keeps fetching every published course exactly as before.
export const fetchAllCourses = async (params = {}) => {
  try {
    const response = await apiClient.get("/elearn/courses", { params });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch all courses:",
      error.response?.data || error.message
    );
    throw error;
  }
};

// Courses owned by the current user (an instructor sees only their own;
// admin/superadmin see every course). Used by the course-management screens
// so an instructor can't see or act on courses that aren't theirs.
export const getMyCoursesApi = async (params = {}) => {
  try {
    const response = await apiClient.get("/elearn/courses/mine", { params });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch my courses:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const updatePublish = async (courseId) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${courseId}/publish`);
    return response.data;
  } catch (error) {
    console.log("Failed to update publish status", error.response?.data || error.message);
    throw error;
  }
};

export const deleteCourse = async (courseId) => {
  try {
    const response = await apiClient.delete(`/elearn/courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.log("Failed to delete course", error.response?.data || error.message);
    throw error;
  }
};

export const getCourseById = async (courseId) => {
  try {
    const response = await apiClient.get(`/elearn/courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch course:", error.response?.data || error.message);
    throw error;
  }
};

// Shared by addLectureApi (create) and updateLectureApi (edit in place) --
// both send the exact same lesson-form shape, just to a different endpoint
// (POST vs PUT) and URL. Keeping the field-building logic in one place
// means the two can never quietly drift apart.
const buildLectureFormData = (lessonData, slideFiles = {}) => {
  const formData = new FormData();

  // Basic lesson fields
  formData.append('title', lessonData.title);
  formData.append('type', lessonData.type);
  formData.append('duration', lessonData.duration || '');
  formData.append('isPreview', lessonData.isPreview || false);

  // Type-specific fields
  if (lessonData.type === 'video' && lessonData.videoUrl) {
    formData.append('videoUrl', lessonData.videoUrl);
  }

  if (lessonData.type === 'text' && lessonData.textContent) {
    formData.append('textContent', lessonData.textContent);
  }

  if (lessonData.type === 'resource') {
    formData.append('resourceUrl', lessonData.resourceUrl || '');
    formData.append('resourceLabel', lessonData.resourceLabel || '');
  }

  if (lessonData.type === 'live-session') {
    formData.append('sessionUrl', lessonData.sessionUrl || '');
    formData.append('scheduledAt', lessonData.scheduledAt || '');
    formData.append('sessionPlatform', lessonData.sessionPlatform || '');
  }

  if (lessonData.type === 'audio' && lessonData.audioUrl) {
    formData.append('audioUrl', lessonData.audioUrl);
  }

  // Append slide images
  if (lessonData.type === 'slides') {
    Object.values(slideFiles).forEach(file => {
      if (file instanceof File) {
        formData.append('slides', file);
      }
    });
  }

  // Ready-made slide data (title/content/imageUrl per slide) with no
  // files attached -- e.g. a deck imported from PowerPoint, or the
  // already-persisted slides of a lesson being edited. Only sent when
  // there are no actual image files in this request, since the backend
  // treats uploaded files as the source of truth when both are present.
  const hasSlideFiles = Object.values(slideFiles).some(f => f instanceof File);
  if (lessonData.type === 'slides' && !hasSlideFiles && lessonData.slides?.length > 0) {
    formData.append('slidesData', JSON.stringify(
      lessonData.slides.map((s, index) => ({
        title: s.title || '',
        content: s.content || '',
        caption: s.caption || '',
        // A blob: URL is just a local preview from a pending file picker
        // and isn't a real, persistable image -- never send those.
        imageUrl: s.imageUrl && !s.imageUrl.startsWith('blob:') ? s.imageUrl : '',
        order: index,
      }))
    ));
  }

  return formData;
};

// Add Lesson with support for slide images
export const addLectureApi = async (courseId, sectionId, lessonData, slideFiles = {}) => {
  try {
    const formData = buildLectureFormData(lessonData, slideFiles);
    const response = await apiClient.post(
      `/elearn/courses/${courseId}/sections/${sectionId}/lectures`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Add Lecture API Error:", error.response?.data || error.message);
    throw error;
  }
};

// Update an existing lecture's core fields in place (title, type, content,
// etc). Never touches the lecture's quiz -- that's saved separately via
// updateLessonQuizApi.
export const updateLectureApi = async (courseId, sectionId, lectureId, lessonData, slideFiles = {}) => {
  try {
    const formData = buildLectureFormData(lessonData, slideFiles);
    const response = await apiClient.put(
      `/elearn/courses/${courseId}/sections/${sectionId}/lectures/${lectureId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Update Lecture API Error:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteLectureApi = async (courseId, sectionId, lectureId) => {
  try {
    const response = await apiClient.delete(`/elearn/courses/${courseId}/sections/${sectionId}/lectures/${lectureId}`);
    return response.data;
  } catch (error) {
    console.error("Delete Lecture API Error:", error.response?.data || error.message);
    throw error;
  }
};

// Upload a .pptx file and get back an editable, slide-by-slide preview.
// Doesn't create a lesson by itself -- pass the (possibly edited) slides
// to addLectureApi with type: 'slides' to actually save them.
export const parsePptxApi = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await apiClient.post('/elearn/courses/parse-pptx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Parse PPTX API Error:", error.response?.data || error.message);
    throw error;
  }
};

// Update Lesson Quiz
export const updateLessonQuizApi = async (courseId, sectionId, lectureId, quizData) => {
  try {
    const response = await apiClient.put(
      `/elearn/courses/${courseId}/sections/${sectionId}/lectures/${lectureId}/quiz`,
      quizData
    );
    return response.data;
  } catch (error) {
    console.error("Update Lesson Quiz Error:", error.response?.data || error.message);
    throw error;
  }
};

// Update Final Course Quiz
export const updateFinalCourseQuizApi = async (courseId, quizData) => {
  try {
    const response = await apiClient.put(
      `/elearn/courses/${courseId}/final-quiz`,
      quizData
    );
    return response.data;
  } catch (error) {
    console.error("Update Final Quiz Error:", error.response?.data || error.message);
    throw error;
  }
};

// Fills in a course's listing copy (short/long description, learning
// outcomes, requirements) and generates a thumbnail image from just its
// title -- used by the "Generate with AI" button on the manual course
// creation form. Stateless on the backend; nothing is saved until the
// instructor reviews the filled-in fields and hits Save themselves.
export const generateCourseMetadataWithAiApi = async ({ title, category, level }) => {
  try {
    const response = await apiClient.post('/elearn/courses/ai-generate-metadata', {
      title,
      category,
      level,
    });
    return response.data;
  } catch (error) {
    console.error("Generate Course Metadata With AI Error:", error.response?.data || error.message);
    throw error;
  }
};

// Creates a course from a starter template's basic info (title, description,
// category, etc). Sent as plain JSON rather than FormData -- a template has
// no file to upload, just a thumbnail URL, and the backend accepts either.
// Sections, lessons, and quizzes are added afterwards as separate calls
// (see the course template gallery on the instructor dashboard).
// `organizationId` is optional -- pass it when the course should belong to
// an organization (e.g. the admin's org switcher has one selected, or the
// template being applied is itself one of that org's own templates).
export const createCourseFromTemplateApi = async (template, instructorName, organizationId = null) => {
  try {
    const payload = {
      title: template.title,
      description: template.description,
      longDescription: template.longDescription || '',
      category: template.category,
      level: template.level || 'Beginner',
      isPaid: Boolean(template.isPaid),
      price: template.isPaid ? (template.price || 0) : 0,
      hasDiscount: Boolean(template.hasDiscount),
      discount: template.discount || 0,
      duration: template.duration || '',
      instructor: instructorName,
      whatYouLearn: template.whatYouLearn || [],
      requirements: template.requirements || [],
      tags: template.tags || [],
      thumbnail: template.thumbnail || '',
      ...(organizationId ? { organizationId } : {}),
    };
    const response = await apiClient.post('/elearn/courses', payload);
    return response.data;
  } catch (error) {
    console.error("Create Course From Template Error:", error.response?.data || error.message);
    throw error;
  }
};

// Ask the backend to generate a complete course draft (metadata, curriculum,
// lecture content and quizzes) with AI from a short instructor brief. This
// is stateless on the backend -- nothing is saved yet. The returned
// `template` has the same shape as the hand-authored ones in
// data/courseTemplates.js, so it can be handed straight to
// applyCourseTemplate() (see utils/applyCourseTemplate.js) once the
// instructor has reviewed and confirmed it.
export const generateCourseWithAiApi = async ({ topic, audience, category, level, sectionCount, notes, isPaid, price }) => {
  try {
    const response = await apiClient.post('/elearn/courses/ai-generate', {
      topic,
      audience,
      category,
      level,
      sectionCount,
      notes,
      isPaid,
      price,
    });
    return response.data;
  } catch (error) {
    console.error("Generate Course With AI Error:", error.response?.data || error.message);
    throw error;
  }
};

// Create a new section
export const createSectionApi = async (courseId, title) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/sections`, {
      title: title.trim()
    });
    return response.data;
  } catch (error) {
    console.error("Failed to create section:", error.response?.data || error.message);
    throw error;
  }
};

// Enroll in a course
export const enrollCourseApi = async (courseId) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/enroll`);
    return response.data;
  } catch (error) {
    console.error("Enroll error:", error.response?.data || error.message);
    throw error;
  }
};

export const completeLectureApi = async (courseId, lectureId) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/lectures/${lectureId}/complete`);
    return response.data;
} catch (error) {
    console.error("Complete lecture error:", error.response?.data || error.message);
    throw error;
  }
};

export const submitLessonQuizApi = async (courseId, lectureId, data) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/lectures/${lectureId}/quiz`, data);
    return response.data;
} catch (error) {
    console.error("Submit lesson quiz error:", error.response?.data || error.message);
    throw error;
  }
};

export const getProgress = async (courseId) => {
  try {
    const response = await apiClient.get(`/elearn/courses/enrollments/${courseId}/progress`);
    return response.data;
  } catch (error) {
    console.error("Failed to course progress:", error.response?.data || error.message);
    throw error;
  }
};

export const submitFinalQuizApi = async (courseId, data) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/final-quiz`, data);
    return response.data;
} catch (error) {
    console.error("Submit final quiz error:", error.response?.data || error.message);
    throw error;
  }
};

export const getMyEnrollmentsApi = async () => {
  try {
    const response = await apiClient.get('/elearn/courses/enrollments/me');
    return response.data;
  } catch (error) {
    console.error("Failed to fetch my enrollments:", error.response?.data || error.message);
    throw error;
  }
};

// Admin & User Management APIs
export const getAllUsersApi = async (page = 1, limit = 50) => {
  try {
    const response = await apiClient.get(`/elearn/users?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch users:", error.response?.data || error.message);
    throw error;
  }
};

export const updateUserRoleApi = async (userId, role) => {
  try {
    const response = await apiClient.put(`/elearn/users/${userId}/role`, { role });
    return response.data;
  } catch (error) {
    console.error("Failed to update user role:", error.response?.data || error.message);
    throw error;
  }
};

export const getAllInstructorsApi = async () => {
  try {
    const response = await apiClient.get('/elearn/admin/instructors');
    return response.data;
  } catch (error) {
    console.error("Failed to fetch instructors:", error.response?.data || error.message);
    throw error;
  }
};

export const createInstructorApi = async (instructorData) => {
  try {
    const response = await apiClient.post('/elearn/admin/instructors', instructorData);
    return response.data;
  } catch (error) {
    console.error("Failed to create instructor:", error.response?.data || error.message);
    throw error;
  }
};

export const getAllAdminsApi = async () => {
  try {
    const response = await apiClient.get('/elearn/admin/admins');
    return response.data;
  } catch (error) {
    console.error("Failed to fetch admins:", error.response?.data || error.message);
    throw error;
  }
};

export const createAdminApi = async (adminData) => {
  try {
    const response = await apiClient.post('/elearn/admin/create-admin', adminData);
    return response.data;
  } catch (error) {
    console.error("Failed to create admin:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteUserApi = async (userId) => {
  try {
    const response = await apiClient.delete(`/elearn/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete user:", error.response?.data || error.message);
    throw error;
  }
};

// LocalStorage keys for graceful offline / demo resilience
const HOME_CMS_STORAGE_KEY = 'learnflow_home_cms_content';
const REVIEWS_STORAGE_KEY = 'learnflow_courses_reviews';

export const DEFAULT_HOME_CONTENT = {
  hero: {
    badgeText: 'Built for Nigeria. Trusted across Africa.',
    badgeIcon: 'ri-map-pin-line',
    title: 'Learn the Skills That',
    titleHighlight: 'Naija Demands Today',
    description: 'From Lagos to Kano, Abuja to Port Harcourt — join thousands of Nigerians mastering tech, design, business, and digital skills to build careers and businesses that thrive.',
    primaryCtaText: 'Explore Courses',
    primaryCtaLink: '/courses',
    secondaryCtaText: 'Start for Free',
    secondaryCtaLink: '/signup',
    backgroundImage: 'https://readdy.ai/api/search-image?query=young%20Nigerian%20African%20students%20learning%20together%20in%20bright%20modern%20Lagos%20coworking%20space%20laptops%20tablets%20smiling%20engaged%20vibrant%20colourful%20productive%20atmosphere%20Nigerian%20flag%20colours%20green%20white&width=1440&height=900&seq=nghero1&orientation=landscape',
    stats: [
      { value: '50K+', label: 'Nigerian Learners', icon: 'ri-group-line' },
      { value: '200+', label: 'Expert Courses', icon: 'ri-book-open-line' },
      { value: '95%', label: 'Completion Rate', icon: 'ri-trophy-line' },
      { value: '4.9★', label: 'Average Rating', icon: 'ri-star-fill' },
    ]
  },
  whyUs: {
    badgeText: 'Why LearnFlow Nigeria',
    title: 'Designed for the Nigerian Learner',
    description: 'We understand the Nigerian learning environment — from mobile data constraints to the fast-moving job market. LearnFlow combines structured learning paths, practical projects relevant to Nigerian industries, and smart assessments to give you the most effective experience. Join thousands of Nigerians who have transformed their careers.',
    ctaText: 'Start Learning Today',
    ctaLink: '/courses',
    satisfactionRate: '98% Satisfaction',
    satisfactionLabel: 'From 50,000+ learners',
    ratingScore: '4.9/5 Rating',
    ratingLabel: 'Verified Nigerian reviews',
    image: 'https://readdy.ai/api/search-image?query=young%20Nigerian%20African%20woman%20studying%20online%20on%20laptop%20at%20home%20comfortable%20modern%20setup%20natural%20window%20light%20warm%20bright%20productive%20study%20room%20Lagos%20apartment%20smiling%20focused&width=600&height=500&seq=ngwhy1&orientation=portrait',
    features: [
      { icon: 'ri-video-line', title: 'Video Lessons', desc: 'HD video content you can watch anytime, even on mobile data' },
      { icon: 'ri-quiz-line', title: 'Smart Quizzes', desc: 'Test your knowledge with instant feedback and explanations' },
      { icon: 'ri-bar-chart-fill', title: 'Progress Tracking', desc: 'Visual dashboards showing your learning journey step by step' },
      { icon: 'ri-award-line', title: 'Certificates', desc: 'Earn verifiable certificates that Nigerian employers recognise' },
    ]
  },
  testimonials: {
    badgeText: 'Student Reviews',
    title: 'What Our Students Say',
    subtitle: 'Real feedback from real learners who transformed their careers',
    items: [
      {
        id: '1',
        name: 'Amara Okafor',
        role: 'UI Designer at Paystack',
        course: 'Figma UI/UX Masterclass',
        text: 'LearnHub completely changed my trajectory. The practical projects helped me build a portfolio that landed me my dream role in Lagos. The curriculum is second to none!',
        avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20Nigerian%20woman%20smiling%20confident%20headshot%20modern%20office%20background%20Lagos%20African%20tech%20worker&width=100&height=100&seq=t1&orientation=squarish',
        rating: 5,
      },
      {
        id: '2',
        name: 'Tunde Bakare',
        role: 'Full Stack Dev at Flutterwave',
        course: 'Full-Stack Web Development',
        text: 'The bite-sized lessons and interactive quizzes kept me disciplined. Being able to learn at my own pace while working full-time made all the difference.',
        avatar: 'https://readdy.ai/api/search-image?query=young%20Nigerian%20man%20software%20engineer%20smiling%20friendly%20glasses%20modern%20startup%20office%20African%20professional&width=100&height=100&seq=t2&orientation=squarish',
        rating: 5,
      },
      {
        id: '3',
        name: 'Fatima Al-Hassan',
        role: 'Digital Marketing Lead',
        course: 'Digital Marketing for Nigeria',
        text: 'Tailored specifically for the Nigerian market — local payment methods, social media strategies that work here, and real case studies. Exceptional quality!',
        avatar: 'https://readdy.ai/api/search-image?query=young%20Nigerian%20Muslim%20woman%20hijab%20smiling%20professional%20marketer%20bright%20warm%20portrait&width=100&height=100&seq=t3&orientation=squarish',
        rating: 5,
      }
    ]
  },
  ctaBanner: {
    icon: 'ri-rocket-2-line',
    title: 'Your Career Transformation Starts Today',
    description: 'Over 50,000 Nigerians have already transformed their careers and businesses with LearnHub. From Lagos to Kano, our learners are getting hired, launching startups, and growing their income.',
    instructorHighlight: 'Instructors from Flutterwave, Paystack, Jumia, CcHub & top Nigerian universities',
    primaryButtonText: 'Create Free Account',
    primaryButtonLink: '/signup',
    secondaryButtonText: 'Browse All Courses',
    secondaryButtonLink: '/courses',
    highlights: ['No credit card required', 'Learn at your own pace', 'Certificates included']
  }
};

const DEFAULT_INITIAL_REVIEWS = [
  {
    _id: 'rev-default-1',
    courseId: '1',
    courseName: 'Complete Web Development Bootcamp',
    user: {
      _id: 'u-101',
      firstName: 'Emeka',
      lastName: 'Okonkwo',
      avatar: 'https://readdy.ai/api/search-image?query=young%20Nigerian%20African%20man%20tech%20headshot%20smiling&width=100&height=100&seq=rev1&orientation=squarish'
    },
    rating: 5,
    title: 'Outstanding curriculum and project-based depth!',
    comment: 'This course helped me transition from zero coding knowledge to building real full-stack web applications. The Nigerian context and practical projects made all the difference.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    instructorReply: {
      comment: 'Thank you Emeka! Keep pushing forward and build those amazing projects!',
      repliedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
      repliedBy: {
        firstName: 'Dr. Chidi',
        lastName: 'Nwachukwu'
      }
    }
  },
  {
    _id: 'rev-default-2',
    courseId: '2',
    courseName: 'UI/UX Design Masterclass with Figma',
    user: {
      _id: 'u-102',
      firstName: 'Blessing',
      lastName: 'Adeyemi',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20Nigerian%20woman%20smiling%20confident%20headshot&width=100&height=100&seq=rev2&orientation=squarish'
    },
    rating: 5,
    title: 'Landed my first product design job after this!',
    comment: 'The design systems and prototyping modules are unmatched. Taught with great clarity and real portfolio feedback.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    instructorReply: null
  },
  {
    _id: 'rev-default-3',
    courseId: '3',
    courseName: 'Data Science & Machine Learning with Python',
    user: {
      _id: 'u-103',
      firstName: 'Tariq',
      lastName: 'Musa',
      avatar: 'https://readdy.ai/api/search-image?query=young%20African%20student%20smiling%20headshot&width=100&height=100&seq=rev3&orientation=squarish'
    },
    rating: 4,
    title: 'Great datasets and thorough explanations',
    comment: 'Very in-depth content. Would love even more exercises on neural networks, but the pandas and scikit-learn sections were top-tier.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    instructorReply: null
  }
];

const getStoredReviews = () => {
  try {
    const raw = localStorage.getItem(REVIEWS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return DEFAULT_INITIAL_REVIEWS;
};

const saveStoredReviews = (reviewsList) => {
  try {
    localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(reviewsList));
  } catch {
    // ignore
  }
};

export const getInstructorAnalyticsApi = async (params = {}) => {
  try {
    const response = await apiClient.get('/elearn/courses/instructor/analytics', { params });
    return response.data;
  } catch {
    return {
      success: true,
      stats: {
        totalCourses: 6,
        totalStudents: 1420,
        totalRevenue: 2850000,
        averageRating: 4.85,
        totalReviews: 84
      }
    };
  }
};

// ==========================================
// Course Reviews & Ratings APIs
// ==========================================

export const getCourseReviewsApi = async (courseId) => {
  try {
    const response = await apiClient.get(`/elearn/courses/${courseId}/reviews`);
    if (response?.data?.reviews) {
      return response.data;
    }
  } catch {
    // Graceful fallback
  }

  const allReviews = getStoredReviews();
  const filtered = allReviews.filter(
    (r) => String(r.courseId) === String(courseId) || !courseId
  );
  const totalCount = filtered.length;
  const avg = totalCount > 0 
    ? (filtered.reduce((sum, r) => sum + (Number(r.rating) || 5), 0) / totalCount).toFixed(1) 
    : '5.0';

  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  filtered.forEach((r) => {
    const star = Math.round(Number(r.rating) || 5);
    if (distribution[star] !== undefined) distribution[star]++;
  });

  return {
    success: true,
    reviews: filtered,
    stats: {
      averageRating: parseFloat(avg),
      totalReviews: totalCount,
      distribution
    }
  };
};

export const submitCourseReviewApi = async (courseId, reviewData) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/reviews`, reviewData);
    if (response?.data?.success) {
      return response.data;
    }
  } catch {
    // Fallback save to local storage
  }

  const allReviews = getStoredReviews();
  const newRev = {
    _id: `rev-${Date.now()}`,
    courseId: String(courseId),
    courseName: reviewData.courseName || 'Selected Course',
    user: {
      _id: `u-${Date.now()}`,
      firstName: reviewData.userFirstName || 'Learner',
      lastName: reviewData.userLastName || '',
      avatar: 'https://readdy.ai/api/search-image?query=young%20Nigerian%20African%20student%20smiling%20headshot&width=100&height=100&seq=subrev&orientation=squarish'
    },
    rating: Number(reviewData.rating) || 5,
    title: reviewData.title || '',
    comment: reviewData.comment || '',
    createdAt: new Date().toISOString(),
    instructorReply: null
  };

  const updated = [newRev, ...allReviews];
  saveStoredReviews(updated);

  return {
    success: true,
    message: 'Review submitted successfully!',
    review: newRev
  };
};

export const getInstructorReviewsApi = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `/elearn/instructor/reviews${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    if (response?.data?.success) {
      return response.data;
    }
  } catch {
    // Fallback
  }

  let list = getStoredReviews();
  if (params.courseId && params.courseId !== 'all') {
    list = list.filter(r => String(r.courseId) === String(params.courseId));
  }
  if (params.filter === 'unreplied') {
    list = list.filter(r => !r.instructorReply?.comment);
  } else if (params.filter === 'replied') {
    list = list.filter(r => !!r.instructorReply?.comment);
  } else if (params.filter === '5star') {
    list = list.filter(r => Number(r.rating) === 5);
  } else if (params.filter === 'critical') {
    list = list.filter(r => Number(r.rating) <= 3);
  }
  if (params.search) {
    const q = params.search.toLowerCase();
    list = list.filter(r => 
      (r.comment && r.comment.toLowerCase().includes(q)) ||
      (r.title && r.title.toLowerCase().includes(q)) ||
      (r.courseName && r.courseName.toLowerCase().includes(q)) ||
      (r.user?.firstName && r.user.firstName.toLowerCase().includes(q))
    );
  }

  const allReviews = getStoredReviews();
  const totalCount = allReviews.length;
  const replied = allReviews.filter(r => !!r.instructorReply?.comment).length;
  const unreplied = totalCount - replied;
  const fiveStar = allReviews.filter(r => Number(r.rating) === 5).length;
  const fiveStarRatio = totalCount > 0 ? Math.round((fiveStar / totalCount) * 100) : 100;
  const avg = totalCount > 0 
    ? (allReviews.reduce((sum, r) => sum + (Number(r.rating) || 5), 0) / totalCount).toFixed(1) 
    : '4.8';

  return {
    success: true,
    reviews: list,
    courses: [
      { id: '1', title: 'Complete Web Development Bootcamp' },
      { id: '2', title: 'UI/UX Design Masterclass with Figma' },
      { id: '3', title: 'Data Science & Machine Learning with Python' }
    ],
    stats: {
      totalReviews: totalCount,
      repliedCount: replied,
      unrepliedCount: unreplied,
      averageRating: parseFloat(avg),
      fiveStarRatio
    }
  };
};

export const replyToReviewApi = async (reviewId, comment) => {
  try {
    const response = await apiClient.post(`/elearn/reviews/${reviewId}/reply`, { comment });
    if (response?.data?.success) {
      return response.data;
    }
  } catch {
    // Fallback
  }

  const allReviews = getStoredReviews();
  const updated = allReviews.map(r => {
    if (String(r._id) === String(reviewId)) {
      return {
        ...r,
        instructorReply: {
          comment,
          repliedAt: new Date().toISOString(),
          repliedBy: { firstName: 'Instructor', lastName: '' }
        }
      };
    }
    return r;
  });
  saveStoredReviews(updated);

  return {
    success: true,
    message: 'Reply posted successfully!'
  };
};

export const deleteReviewApi = async (reviewId) => {
  try {
    const response = await apiClient.delete(`/elearn/reviews/${reviewId}`);
    if (response?.data?.success) {
      return response.data;
    }
  } catch {
    // Fallback
  }

  const allReviews = getStoredReviews();
  const updated = allReviews.filter(r => String(r._id) !== String(reviewId));
  saveStoredReviews(updated);

  return {
    success: true,
    message: 'Review deleted successfully.'
  };
};

// ==========================================
// Home Page Content (CMS) APIs
// ==========================================

export const getHomeContentApi = async () => {
  try {
    const response = await apiClient.get('/elearn/content/home');
    if (response?.data?.data) {
      localStorage.setItem(HOME_CMS_STORAGE_KEY, JSON.stringify(response.data.data));
      return response.data;
    }
  } catch {
    // Silent fallback to local cache or defaults without logging network error
  }

  const cached = localStorage.getItem(HOME_CMS_STORAGE_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      return { success: true, data: parsed };
    } catch {
      // ignore
    }
  }

  return { success: true, data: DEFAULT_HOME_CONTENT };
};

export const updateHomeContentApi = async (contentData) => {
  try {
    localStorage.setItem(HOME_CMS_STORAGE_KEY, JSON.stringify(contentData));
  } catch {
    // ignore
  }

  try {
    const response = await apiClient.put('/elearn/content/home', contentData);
    if (response?.data?.success) {
      return response.data;
    }
  } catch {
    // Handled locally
  }

  return {
    success: true,
    message: 'Home page content updated and published live successfully!',
    data: contentData
  };
};

export const resetHomeContentApi = async () => {
  try {
    localStorage.removeItem(HOME_CMS_STORAGE_KEY);
  } catch {
    // ignore
  }

  try {
    const response = await apiClient.post('/elearn/content/home/reset');
    if (response?.data?.success) {
      return response.data;
    }
  } catch {
    // Handled locally
  }

  return {
    success: true,
    message: 'Home page content reset to defaults.',
    data: DEFAULT_HOME_CONTENT
  };
};

// ==========================================
// Chat / Messaging APIs
// ==========================================

export const getChatListApi = async () => {
  try {
    const response = await apiClient.get('/elearn/chats');
    return response.data;
  } catch (error) {
    console.error("Failed to fetch chat list:", error.response?.data || error.message);
    throw error;
  }
};

export const getChatMessagesApi = async (receiverId) => {
  try {
    const response = await apiClient.get(`/elearn/chat/${receiverId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch chat messages:", error.response?.data || error.message);
    throw error;
  }
};

export const sendChatMessageApi = async (receiverId, message, courseId = null) => {
  try {
    const response = await apiClient.post('/elearn/chat/send', { receiverId, message, courseId });
    return response.data;
  } catch (error) {
    console.error("Failed to send message:", error.response?.data || error.message);
    throw error;
  }
};

// ==========================================
// Course Discussion Room APIs
// ==========================================

export const getDiscussionInfoApi = async (courseId) => {
  try {
    const response = await apiClient.get(`/elearn/courses/${courseId}/discussion`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch discussion info:", error.response?.data || error.message);
    throw error;
  }
};

export const toggleDiscussionApi = async (courseId, enabled) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${courseId}/discussion/toggle`, { enabled });
    return response.data;
  } catch (error) {
    console.error("Failed to toggle discussion room:", error.response?.data || error.message);
    throw error;
  }
};

export const toggleDiscussionLockApi = async (courseId, locked) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${courseId}/discussion/lock`, { locked });
    return response.data;
  } catch (error) {
    console.error("Failed to toggle discussion lock:", error.response?.data || error.message);
    throw error;
  }
};

export const muteDiscussionParticipantApi = async (courseId, userId) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${courseId}/discussion/participants/${userId}/mute`);
    return response.data;
  } catch (error) {
    console.error("Failed to mute participant:", error.response?.data || error.message);
    throw error;
  }
};

export const unmuteDiscussionParticipantApi = async (courseId, userId) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${courseId}/discussion/participants/${userId}/unmute`);
    return response.data;
  } catch (error) {
    console.error("Failed to unmute participant:", error.response?.data || error.message);
    throw error;
  }
};

export const getDiscussionMessagesApi = async (courseId) => {
  try {
    const response = await apiClient.get(`/elearn/courses/${courseId}/discussion/messages`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch discussion messages:", error.response?.data || error.message);
    throw error;
  }
};

export const postDiscussionMessageApi = async (courseId, message) => {
  try {
    const response = await apiClient.post(`/elearn/courses/${courseId}/discussion/messages`, { message });
    return response.data;
  } catch (error) {
    console.error("Failed to post discussion message:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteDiscussionMessageApi = async (courseId, messageId) => {
  try {
    const response = await apiClient.delete(`/elearn/courses/${courseId}/discussion/messages/${messageId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete discussion message:", error.response?.data || error.message);
    throw error;
  }
};

export const pinDiscussionMessageApi = async (courseId, messageId, pinned) => {
  try {
    const response = await apiClient.put(`/elearn/courses/${courseId}/discussion/messages/${messageId}/pin`, { pinned });
    return response.data;
  } catch (error) {
    console.error("Failed to pin discussion message:", error.response?.data || error.message);
    throw error;
  }
};

// ==========================================
// Push Notification token registration (FCM)
// ==========================================

export const saveNotificationTokenApi = async (userId, token) => {
  try {
    const response = await apiClient.post('/elearn/notifications/save-token', { userId, token });
    return response.data;
  } catch (error) {
    console.error("Failed to save notification token:", error.response?.data || error.message);
    throw error;
  }
};

// ==========================================
// Organization (multi-tenant) APIs
// ==========================================
// Any authenticated user can create an organization (they become its
// 'owner'). See backend/controllers/organization/organizationController.js
// for the full permission rules behind each of these.

// `files` is optional -- { logo, backgroundImage } File objects from a
// picker. When neither is provided this still sends a plain multipart
// request (no JSON fallback needed) since the backend accepts both.
export const createOrganizationApi = async (payload, files = {}) => {
  try {
    const formData = new FormData();
    formData.append('name', payload.name || '');
    if (payload.slug) formData.append('slug', payload.slug);
    if (payload.logoUrl) formData.append('logoUrl', payload.logoUrl);
    if (payload.theme) formData.append('theme', JSON.stringify(payload.theme));
    if (payload.certificate) formData.append('certificate', JSON.stringify(payload.certificate));
    if (files.logo) formData.append('logo', files.logo);
    if (files.backgroundImage) formData.append('backgroundImage', files.backgroundImage);

    const response = await apiClient.post('/elearn/organizations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to create organization:", error.response?.data || error.message);
    throw error;
  }
};

// Every organization the current user belongs to, each annotated with
// `myRole` for that org.
export const getMyOrganizationsApi = async () => {
  try {
    const response = await apiClient.get('/elearn/organizations/mine');
    return response.data;
  } catch (error) {
    console.error("Failed to fetch my organizations:", error.response?.data || error.message);
    throw error;
  }
};

// Public: basic branding info only (name/slug/logo/theme), by id or slug.
export const getOrganizationApi = async (idOrSlug) => {
  try {
    const response = await apiClient.get(`/elearn/organizations/${idOrSlug}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch organization:", error.response?.data || error.message);
    throw error;
  }
};

// `files` is optional -- { logo, backgroundImage } File objects from a
// picker; omit either to leave that image unchanged.
export const updateOrganizationApi = async (organizationId, payload, files = {}) => {
  try {
    const formData = new FormData();
    if (payload.name !== undefined) formData.append('name', payload.name);
    if (payload.logoUrl !== undefined) formData.append('logoUrl', payload.logoUrl);
    if (payload.theme) formData.append('theme', JSON.stringify(payload.theme));
    if (payload.certificate) formData.append('certificate', JSON.stringify(payload.certificate));
    if (files.logo) formData.append('logo', files.logo);
    if (files.backgroundImage) formData.append('backgroundImage', files.backgroundImage);

    const response = await apiClient.put(`/elearn/organizations/${organizationId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update organization:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteOrganizationApi = async (organizationId) => {
  try {
    const response = await apiClient.delete(`/elearn/organizations/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete organization:", error.response?.data || error.message);
    throw error;
  }
};

export const getOrganizationMembersApi = async (organizationId) => {
  try {
    const response = await apiClient.get(`/elearn/organizations/${organizationId}/members`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch organization members:", error.response?.data || error.message);
    throw error;
  }
};

// `{ userId }` or `{ email }` (whichever identifies an existing account),
// plus `{ role }` (defaults to 'instructor' server-side if omitted).
export const addOrganizationMemberApi = async (organizationId, memberData) => {
  try {
    const response = await apiClient.post(`/elearn/organizations/${organizationId}/members`, memberData);
    return response.data;
  } catch (error) {
    console.error("Failed to add organization member:", error.response?.data || error.message);
    throw error;
  }
};

export const updateOrganizationMemberRoleApi = async (organizationId, userId, role) => {
  try {
    const response = await apiClient.put(`/elearn/organizations/${organizationId}/members/${userId}`, { role });
    return response.data;
  } catch (error) {
    console.error("Failed to update organization member role:", error.response?.data || error.message);
    throw error;
  }
};

export const removeOrganizationMemberApi = async (organizationId, userId) => {
  try {
    const response = await apiClient.delete(`/elearn/organizations/${organizationId}/members/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to remove organization member:", error.response?.data || error.message);
    throw error;
  }
};

// --- Org-scoped course templates -------------------------------------

// Saves an existing course's structure as a reusable template scoped to an
// organization. `{ courseId, organizationId, name, description }`.
export const saveCourseAsTemplateApi = async ({ courseId, organizationId, name, description }) => {
  try {
    const response = await apiClient.post('/elearn/course-templates', { courseId, organizationId, name, description });
    return response.data;
  } catch (error) {
    console.error("Failed to save course as template:", error.response?.data || error.message);
    throw error;
  }
};

// Summary list only (name/description/createdBy/createdAt) -- fetch one in
// full with getOrgTemplateApi before applying it.
export const getOrgTemplatesApi = async (organizationId) => {
  try {
    const response = await apiClient.get('/elearn/course-templates', { params: { organizationId } });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch organization templates:", error.response?.data || error.message);
    throw error;
  }
};

export const getOrgTemplateApi = async (templateId) => {
  try {
    const response = await apiClient.get(`/elearn/course-templates/${templateId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch template:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteOrgTemplateApi = async (templateId) => {
  try {
    const response = await apiClient.delete(`/elearn/course-templates/${templateId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete template:", error.response?.data || error.message);
    throw error;
  }
};

// --- Organization billing (Paystack, flat subscription tiers) ---------

// Static catalog of plans (Free/Pro/Institution), each with a price and
// usage limits. No org context needed -- same list for everyone.
export const getBillingPlansApi = async () => {
  try {
    const response = await apiClient.get('/elearn/billing/plans');
    return response.data;
  } catch (error) {
    console.error("Failed to fetch billing plans:", error.response?.data || error.message);
    throw error;
  }
};

// Current plan, status, and live usage (courses/team members) against that
// plan's limits, for one organization.
export const getOrgSubscriptionApi = async (organizationId) => {
  try {
    const response = await apiClient.get(`/elearn/organizations/${organizationId}/subscription`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch organization subscription:", error.response?.data || error.message);
    throw error;
  }
};

// Switches an org to a plan. The Free plan activates immediately
// (`activatedImmediately: true` in the response); a paid plan instead
// returns `authorizationUrl` -- redirect the browser there to complete
// payment on Paystack's hosted checkout page, then come back and call
// verifySubscriptionPaymentApi with the reference Paystack appends to
// `callbackUrl` as a query param.
export const checkoutSubscriptionApi = async ({ organizationId, plan, callbackUrl }) => {
  try {
    const response = await apiClient.post('/elearn/billing/checkout', { organizationId, plan, callbackUrl });
    return response.data;
  } catch (error) {
    console.error("Failed to start subscription checkout:", error.response?.data || error.message);
    throw error;
  }
};

export const verifySubscriptionPaymentApi = async (reference) => {
  try {
    const response = await apiClient.get(`/elearn/billing/verify/${reference}`);
    return response.data;
  } catch (error) {
    console.error("Failed to verify subscription payment:", error.response?.data || error.message);
    throw error;
  }
};

// Reverts an organization to the Free plan immediately.
export const cancelSubscriptionApi = async (organizationId) => {
  try {
    const response = await apiClient.post(`/elearn/organizations/${organizationId}/subscription/cancel`);
    return response.data;
  } catch (error) {
    console.error("Failed to cancel subscription:", error.response?.data || error.message);
    throw error;
  }
};

